import { useEffect, useMemo, useState } from "react";
import Seo from "@/components/base/Seo";
import StatusBadge from "@/components/base/StatusBadge";
import DataTable, { type DataColumn } from "@/components/base/DataTable";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import AdminPageHeader from "../components/AdminPageHeader";
import AdminToolbar from "../components/AdminToolbar";
import AdminFilterTabs from "../components/AdminFilterTabs";
import { useAuth } from "@/context/AuthContext";
import {
  createAdminSubscription,
  deleteAdminSubscription,
  getSubscriptions,
  updateAdminSubscription,
  type SubscriptionInput,
} from "@/services/adminApi";
import type { Subscription } from "@/types";

const statusTabs = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "trialing", label: "Trialing" },
  { value: "past_due", label: "Past due" },
  { value: "cancelled", label: "Cancelled" },
];

const STATUS_OPTIONS: Subscription["status"][] = ["active", "trialing", "past_due", "cancelled"];
const CYCLE_OPTIONS: Subscription["cycle"][] = ["Monthly", "Annual"];

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";
const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500";

interface DraftState {
  plan: string;
  amount: string;
  cycle: Subscription["cycle"];
  renewsAt: string;
  status: Subscription["status"];
}

const emptyDraft: DraftState = { plan: "", amount: "", cycle: "Monthly", renewsAt: "", status: "active" };

export default function AdminSubscriptions() {
  const { token } = useAuth();
  const [rows, setRows] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState<DraftState>(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Subscription | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getSubscriptions(token)
      .then((s) => active && setRows(s))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2200);
  };

  const flashError = (msg: string) => {
    setActionError(msg);
    setTimeout(() => setActionError(""), 3200);
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return rows.filter((r) => {
      const matchesStatus = status === "all" || r.status === status;
      const matchesQuery =
        !q || r.business.toLowerCase().includes(q) || r.owner.toLowerCase().includes(q) || r.plan.toLowerCase().includes(q);
      return matchesStatus && matchesQuery;
    });
  }, [rows, query, status]);

  const stats = useMemo(() => {
    const active = rows.filter((r) => r.status === "active" || r.status === "trialing");
    const monthly = active.reduce((s, r) => s + (r.cycle === "Annual" ? r.amount / 12 : r.amount), 0);
    const pastDue = rows.filter((r) => r.status === "past_due").length;
    return { activeCount: active.length, mrr: Math.round(monthly), pastDue };
  }, [rows]);

  const resetForm = () => {
    setDraft(emptyDraft);
    setEditingId(null);
    setShowForm(false);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: SubscriptionInput = {
      plan: draft.plan.trim() || undefined,
      amount: draft.amount ? Number(draft.amount) : 0,
      cycle: draft.cycle,
      renewsAt: draft.renewsAt.trim() || undefined,
      status: draft.status,
    };
    setSaving(true);

    if (editingId) {
      const ok = await updateAdminSubscription(editingId, payload, token);
      if (!ok) {
        setSaving(false);
        flashError("Couldn't save that subscription. Please try again.");
        return;
      }
      setRows((prev) => prev.map((r) => (r.id === editingId ? { ...r, ...payload, amount: payload.amount ?? r.amount } : r)));
      flash("Subscription updated.");
    } else {
      const created = await createAdminSubscription(payload, token);
      if (!created) {
        setSaving(false);
        flashError("Couldn't create that subscription. Please try again.");
        return;
      }
      setRows((prev) => [created, ...prev]);
      flash("Subscription created.");
    }

    setSaving(false);
    resetForm();
  };

  const startEdit = (row: Subscription) => {
    setEditingId(row.id);
    setDraft({ plan: row.plan, amount: String(row.amount), cycle: row.cycle, renewsAt: row.renewsAt, status: row.status });
    setShowForm(true);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const row = pendingDelete;
    setDeleting(true);
    const snapshot = rows;
    setRows((prev) => prev.filter((r) => r.id !== row.id));
    const ok = await deleteAdminSubscription(row.id, token);
    setDeleting(false);
    setPendingDelete(null);
    if (!ok) {
      setRows(snapshot);
      flashError("Couldn't remove that subscription. Please try again.");
      return;
    }
    if (editingId === row.id) resetForm();
    flash("Subscription removed.");
  };

  const columns: DataColumn<Subscription>[] = [
    {
      key: "business",
      header: "Business",
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate font-semibold text-foreground-950">{r.business}</p>
          <p className="truncate text-xs text-foreground-500">{r.owner}</p>
        </div>
      ),
    },
    {
      key: "plan",
      header: "Plan",
      nowrap: true,
      render: (r) => (
        <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-1 text-[11px] font-bold text-primary-900">
          <i className="ri-vip-crown-line"></i> {r.plan || "—"}
        </span>
      ),
    },
    { key: "cycle", header: "Billing", render: (r) => <span className="text-foreground-700">{r.cycle}</span>, nowrap: true },
    { key: "amount", header: "Amount", align: "right", render: (r) => <span className="font-semibold text-foreground-900">₹{r.amount.toLocaleString("en-IN")}</span>, nowrap: true },
    { key: "renewsAt", header: "Renews", render: (r) => <span className="text-foreground-500">{r.renewsAt || "—"}</span>, nowrap: true },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} />, nowrap: true },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      nowrap: true,
      render: (r) => (
        <div className="flex items-center justify-end gap-1.5">
          <button
            type="button"
            onClick={() => startEdit(r)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-primary-400 cursor-pointer"
            aria-label="Edit subscription"
          >
            <i className="ri-edit-line"></i>
          </button>
          <button
            type="button"
            onClick={() => setPendingDelete(r)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-secondary-300 text-secondary-700 hover:bg-secondary-50 cursor-pointer"
            aria-label="Delete subscription"
          >
            <i className="ri-delete-bin-line"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <Seo title="Subscriptions" path="/admin/subscriptions" />

      <AdminPageHeader
        icon="ri-repeat-line"
        title="Subscriptions"
        subtitle="Recurring plan revenue across every business owner."
        actions={
          <>
            {actionError ? (
              <span className="inline-flex items-center gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-2 text-sm text-secondary-900">
                <i className="ri-error-warning-line text-secondary-600"></i> {actionError}
              </span>
            ) : notice ? (
              <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
                <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => (showForm ? resetForm() : setShowForm(true))}
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className={showForm ? "ri-close-line" : "ri-add-line"}></i> {showForm ? "Cancel" : "New subscription"}
            </button>
          </>
        }
      />

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Active subscriptions</span>
          <p className="mt-2 font-heading text-2xl font-extrabold text-foreground-950">{stats.activeCount}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Est. monthly revenue</span>
          <p className="mt-2 font-heading text-2xl font-extrabold text-accent-700">₹{stats.mrr.toLocaleString("en-IN")}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Past due</span>
          <p className="mt-2 font-heading text-2xl font-extrabold text-secondary-700">{stats.pastDue}</p>
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-3 rounded-lg border border-background-200 bg-background-50 p-4 sm:grid-cols-2 lg:grid-cols-6">
          <div className="lg:col-span-2">
            <label htmlFor="subPlan" className={labelClass}>Plan</label>
            <input id="subPlan" value={draft.plan} onChange={(e) => setDraft((d) => ({ ...d, plan: e.target.value }))} placeholder="Premium" className={inputClass} />
          </div>
          <div>
            <label htmlFor="subAmount" className={labelClass}>Amount (₹)</label>
            <input id="subAmount" type="number" min="0" value={draft.amount} onChange={(e) => setDraft((d) => ({ ...d, amount: e.target.value }))} placeholder="0" className={inputClass} />
          </div>
          <div>
            <label htmlFor="subCycle" className={labelClass}>Billing</label>
            <select id="subCycle" value={draft.cycle} onChange={(e) => setDraft((d) => ({ ...d, cycle: e.target.value as Subscription["cycle"] }))} className={inputClass}>
              {CYCLE_OPTIONS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="subRenews" className={labelClass}>Renews</label>
            <input id="subRenews" type="date" value={draft.renewsAt} onChange={(e) => setDraft((d) => ({ ...d, renewsAt: e.target.value }))} className={inputClass} />
          </div>
          <div>
            <label htmlFor="subStatus" className={labelClass}>Status</label>
            <select id="subStatus" value={draft.status} onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value as Subscription["status"] }))} className={inputClass}>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2 lg:col-span-6 flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap cursor-pointer"
            >
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
              {editingId ? "Save changes" : "Save subscription"}
            </button>
          </div>
        </form>
      )}

      <div className="mt-6">
        <AdminToolbar query={query} onQuery={setQuery} placeholder="Search subscriptions…">
          <AdminFilterTabs options={statusTabs} value={status} onChange={setStatus} />
        </AdminToolbar>
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="h-96 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
        ) : error ? (
          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6 text-center text-sm text-secondary-900">
            Couldn't load subscriptions. Please refresh.
          </div>
        ) : (
          <DataTable columns={columns} rows={filtered} rowKey={(r) => r.id} minWidth="980px" />
        )}
      </div>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete this subscription?"
        message={pendingDelete ? `The ${pendingDelete.plan || "selected"} subscription for “${pendingDelete.business}” will be permanently removed.` : ""}
        confirmLabel="Delete subscription"
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}